import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import axios from "axios";
import { useEffect, useState } from "react";
import { hostname } from "../../utils";
import ReactMarkdown from "react-markdown";

// Get dialog title based on action type
const getDialogTitle = (actionType: string | undefined) => {
  switch (actionType) {
    case "http_request":
      return "HTTP Request Details";
    case "browser_task":
      return "Browser Task Summary";
    case "agent":
    default:
      return "Agent Research Summary";
  }
};

// Build markdown steps from raw HTTP data
const buildHttpSteps = (researchSummary: any): string[] => {
  if (!researchSummary) return [];

  const steps: string[] = [];
  const request = researchSummary.request || {};
  const response = researchSummary.response || {};

  // Request info
  if (request.method || request.url) {
    steps.push(`**Request**\n${request.method || "GET"} ${request.url || ""}`);
  }

  // Request headers
  if (request.headers && Object.keys(request.headers).length > 0) {
    steps.push(`**Request Headers**\n\`\`\`json\n${JSON.stringify(request.headers, null, 2)}\n\`\`\``);
  }

  // Request body
  if (request.body) {
    steps.push(`**Request Body**\n\`\`\`\n${request.body}\n\`\`\``);
  }

  // Response status and duration
  const statusCode = researchSummary.statusCode || response.statusCode;
  const duration = researchSummary.duration;
  if (statusCode || duration) {
    let statusLine = "**Response**\n";
    if (statusCode) statusLine += `Status: ${statusCode}`;
    if (duration) statusLine += `\nDuration: ${duration.toFixed(0)}ms`;
    steps.push(statusLine);
  }

  // Response body
  if (response.body) {
    steps.push(`**Response Body**\n\`\`\`\n${response.body}\n\`\`\``);
  }

  return steps;
};

export default ({
  open,
  onClose,
  listId,
  selectedHeader,
  selectedRowId,
  companyId,
  getToken,
  eventListener,
}: {
  open: boolean;
  onClose: () => void;
  selectedHeader: any;
  selectedRowId: string | null;
  listId: string;
  companyId: string;
  getToken: Function;
  eventListener: Function;
}) => {
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<Array<any>>([]);
  const [researchSummary, setResearchSummary] = useState<any>(null);

  const handleClose = () => {
    setSteps([]);
    setResearchSummary(null);
    onClose();
  };

  const loadSummary = async () => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .get(
        `${hostname}/lists/${listId}/header/${selectedHeader.id}/rows/${selectedRowId}`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      )
      .then((res) => {
        setSteps(res.data.researchSteps || []);
        setResearchSummary(res.data.researchSummary || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedHeader) {
      loadSummary();
    }
  }, [selectedHeader]);

  if (!selectedHeader || !selectedRowId) {
    return <></>;
  }

  const actionType = selectedHeader?.actionParameters?.actionType;
  const isHttpRequest = actionType === "http_request";

  // For HTTP requests, build steps from raw data; otherwise use stored steps
  const displaySteps = isHttpRequest ? buildHttpSteps(researchSummary) : steps;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        {getDialogTitle(actionType)}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress />}
        {!loading && displaySteps.length === 0 && !researchSummary?.summary && (
          <Typography>No details found</Typography>
        )}

        {/* HTTP Request Status Header */}
        {!loading && isHttpRequest && researchSummary && (
          <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            {researchSummary.success ? (
              <Chip
                icon={<CheckCircleIcon />}
                label={`HTTP ${researchSummary.statusCode}`}
                color="primary"
              />
            ) : (
              <Chip
                icon={<ErrorIcon />}
                label={researchSummary.error ? "Error" : `HTTP ${researchSummary.statusCode}`}
                color="error"
              />
            )}
            {researchSummary.duration != null && (
              <Typography variant="body2" color="text.secondary">
                {researchSummary.duration.toFixed(0)}ms
              </Typography>
            )}
            {researchSummary.error && (
              <Typography variant="body2" color="error">
                {researchSummary.error}
              </Typography>
            )}
          </Box>
        )}

        {/* Steps/Details Cards */}
        {displaySteps.map((step: any, i: number) => (
          <Card sx={{ mt: 2 }} key={i}>
            <CardContent>
              {!isHttpRequest && (
                <Typography gutterBottom>Step #{i + 1}</Typography>
              )}
              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  "& pre": {
                    backgroundColor: "action.hover",
                    p: 2,
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 300,
                    fontSize: "0.85rem",
                  },
                  "& code": {
                    backgroundColor: "action.hover",
                    px: 0.5,
                    borderRadius: 0.5,
                    fontSize: "0.85rem",
                  },
                }}
              >
                <ReactMarkdown>{step}</ReactMarkdown>
              </Box>
            </CardContent>
          </Card>
        ))}

        {/* Non-HTTP Summary (for agent actions) */}
        {!isHttpRequest && researchSummary?.summary && (
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold" }}>
                Final Summary
              </Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {researchSummary.summary}
              </Typography>
            </CardContent>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
